import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dav3ejmoc082o2i1jtkg";

const PERKS = [
  "Set your own daily price and availability",
  "Get paid straight to M-Pesa after every trip",
  "Free protection cover on every booking",
];

export default function HostCta() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value.trim());
      }
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, { method: "POST", body: payload });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");

      if (!response.ok || (parsed?.code && parsed.code !== "OK") || isSpam) {
        setStatus("error");
        setFormError(serverMessage || "We couldn't send that. Please try again.");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("Network problem. Please try again in a moment.");
    }
  }

  return (
    <div id="host" className="mx-auto w-full max-w-7xl px-4 py-16 md:px-8 md:py-24">
      <Reveal>
        <div className="overflow-hidden rounded-3xl border border-background-200/70">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="relative min-h-[280px] w-full lg:min-h-full">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20evening%20photograph%20of%20a%20smiling%20Kenyan%20car%20owner%20standing%20beside%20his%20clean%20dark%20SUV%20on%20a%20quiet%20Nairobi%20residential%20street%2C%20soft%20golden%20hour%20light%2C%20deep%20shadows%2C%20moody%20dark%20tones%20with%20amber%20highlights%2C%20premium%20editorial%20lifestyle%20photography%2C%20shallow%20depth%20of%20field&width=1400&height=900&seq=gari-host-cta-01&orientation=landscape"
                alt="A Kenyan host preparing their car for a Gari rental in Nairobi"
                title="Become a Gari host in Kenya and earn from your car"
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50/80 via-background-50/20 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="font-heading text-2xl font-semibold text-foreground-950">
                  Your car could earn KSh 250,000 a year
                </p>
                <p className="mt-1 text-sm text-foreground-600">Most Gari hosts rent out just a few days a month.</p>
              </div>
            </div>

            <div className="bg-background-100 p-6 md:p-10">
              <span className="text-sm font-medium uppercase tracking-[0.2em] text-primary-500">Become a host</span>
              <h2 className="mt-3 font-heading text-3xl font-semibold text-foreground-950">
                Turn your parked car into income
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                Join hundreds of Kenyan owners earning from their vehicles. List in minutes — we handle the
                bookings, payments and support.
              </p>

              <ul className="mt-6 space-y-3">
                {PERKS.map((perk) => (
                  <li key={perk} className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <i className="ri-check-line mt-0.5 text-accent-400"></i>
                    {perk}
                  </li>
                ))}
              </ul>

              <Link
                to="/host"
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/60"
              >
                <i className="ri-add-circle-line text-primary-500"></i> List your car right away
              </Link>

              <form id="gari-host-enquiry" data-readdy-form onSubmit={handleSubmit} className="mt-8 space-y-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <input
                    type="text"
                    name="full_name"
                    required
                    placeholder="Full name"
                    autoComplete="name"
                    className="w-full rounded-lg border border-background-200/70 bg-background-50 px-3 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                  />
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="Email address"
                    autoComplete="email"
                    className="w-full rounded-lg border border-background-200/70 bg-background-50 px-3 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="M-Pesa number"
                    autoComplete="tel"
                    className="w-full rounded-lg border border-background-200/70 bg-background-50 px-3 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                  />
                  <input
                    type="text"
                    name="city"
                    required
                    placeholder="City (e.g. Nairobi)"
                    className="w-full rounded-lg border border-background-200/70 bg-background-50 px-3 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <textarea
                  name="vehicle_details"
                  rows={3}
                  maxLength={500}
                  placeholder="Tell us about your car (make, model, year)"
                  className="w-full rounded-lg border border-background-200/70 bg-background-50 px-3 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                />
                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="field-extra"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i> Sending
                    </>
                  ) : (
                    <>
                      <i className="ri-hand-coin-line"></i> Start earning
                    </>
                  )}
                </button>
                {status === "success" && (
                  <p className="flex items-center gap-1.5 text-sm text-accent-400">
                    <i className="ri-checkbox-circle-line"></i> Asante! Our team will reach out within 24 hours.
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-center gap-1.5 text-sm text-secondary-300">
                    <i className="ri-error-warning-line"></i> {formError}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  );
}